/**
 * /my/email-preferences — per-user opt-in/out of Lenavio email categories.
 *
 * Self-service only. Transactional mail (approvals, billing receipts,
 * account notices) isn't surfaced here because it's not opt-outable —
 * the back-end ``OPT_OUTABLE_EMAIL_CATEGORIES`` set defines what the
 * server lets us toggle, and we fetch that list dynamically so this
 * page keeps working if we add new categories later.
 *
 * Optimistic-update pattern: toggle immediately, revert on failure and
 * toast. Avoids a full-page spinner on every flip.
 */
import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ShieldCheck } from "lucide-react";
import { pageTransition } from "@/lib/utils";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card } from "@/components/ui/Card";
import { Skeleton } from "@/components/ui/Skeleton";
import { useToast } from "@/hooks/useToast";
import {
  getMyEmailPreferences,
  listEmailCategories,
  updateMyEmailPreference,
  type EmailCategoryMeta,
} from "@/api/emailPreferences";

export function EmailPreferencesPage() {
  const [categories, setCategories] = useState<EmailCategoryMeta[] | null>(null);
  const [optedOut, setOptedOut] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const { addToast } = useToast();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [cats, prefs] = await Promise.all([
          listEmailCategories(),
          getMyEmailPreferences(),
        ]);
        if (cancelled) return;
        setCategories(cats);
        setOptedOut(prefs.preferences);
      } catch (err) {
        if (!cancelled) {
          addToast(
            "error",
            "Could not load your email preferences. Try refreshing the page.",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [addToast]);

  const handleToggle = useCallback(
    async (category: string, nextOptedOut: boolean) => {
      const prev = optedOut[category];
      // Optimistic update
      setOptedOut((s) => ({ ...s, [category]: nextOptedOut }));
      setSaving(category);
      try {
        const res = await updateMyEmailPreference(category, nextOptedOut);
        setOptedOut(res.preferences);
      } catch (err) {
        // Revert on failure
        setOptedOut((s) => ({ ...s, [category]: prev }));
        addToast(
          "error",
          "Couldn't save that change. Try again in a moment.",
        );
      } finally {
        setSaving(null);
      }
    },
    [optedOut, addToast],
  );

  return (
    <motion.div {...pageTransition} className="space-y-6">
      <PageHeader
        title="Email preferences"
        subtitle="Choose which Lenavio emails you want to receive."
      />

      <Card>
        <div className="flex items-start gap-3 mb-5">
          <div className="rounded-lg bg-accent/10 p-2 text-accent">
            <ShieldCheck className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <h3 className="text-sm font-semibold font-heading text-text-primary">
              You're always opted in to account and approval mail.
            </h3>
            <p className="text-xs text-text-muted mt-0.5">
              Transactional and legally-required emails — login codes,
              approval requests, billing receipts, and platform notices —
              aren't listed here and can't be disabled.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="space-y-3">
            <Skeleton className="h-14" />
            <Skeleton className="h-14" />
            <Skeleton className="h-14" />
          </div>
        ) : categories && categories.length > 0 ? (
          <ul className="divide-y divide-border">
            {categories.map((cat) => {
              const isOptedOut = Boolean(optedOut[cat.category]);
              const disabled = saving === cat.category;
              return (
                <li
                  key={cat.category}
                  className="flex items-start justify-between py-4 gap-4"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-text-primary">
                      {cat.label}
                    </p>
                    <p className="text-xs text-text-muted mt-0.5">
                      {cat.description}
                    </p>
                  </div>
                  <label
                    className={`inline-flex items-center cursor-pointer shrink-0 ${
                      disabled ? "opacity-60 pointer-events-none" : ""
                    }`}
                  >
                    <span className="sr-only">
                      {isOptedOut ? "Enable" : "Disable"} {cat.label}
                    </span>
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={!isOptedOut}
                      disabled={disabled}
                      onChange={(e) =>
                        handleToggle(cat.category, !e.target.checked)
                      }
                    />
                    <span className="w-10 h-5 rounded-full bg-border peer-checked:bg-accent transition-colors relative after:absolute after:left-0.5 after:top-0.5 after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-transform peer-checked:after:translate-x-5" />
                  </label>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="text-sm text-text-muted py-4">
            No optional email categories are available right now.
          </p>
        )}
      </Card>

      <Card>
        <h3 className="text-sm font-semibold font-heading text-text-primary mb-1">
          How one-click unsubscribe works
        </h3>
        <p className="text-xs text-text-muted leading-relaxed">
          Every optional email from Lenavio includes a signed unsubscribe
          link in the footer. Clicking it takes you straight to a
          confirmation page — no login required. Gmail and Apple Mail
          also surface a native "Unsubscribe" button next to the subject
          line, which goes to the same place. Changes made that way
          always show up here.
        </p>
      </Card>
    </motion.div>
  );
}
